import { useEffect, useState, useCallback } from "react";
import { AlertCircle, CalendarClock, Bell, Clock, CheckCircle2, Sparkles } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { TaskRow } from "@/components/tasks/TaskListPremium";
import { LoadingState } from "@/components/states";

const today = () => new Date().toISOString().slice(0, 10);

// Calm personal command centre. Sections render only when real data exists.
export function TodayCommand({ catName, onOpen, refreshKey, onComplete }) {
  const [tasks, setTasks] = useState([]);
  const [done, setDone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [all, completed] = await Promise.all([apiFetch("/tasks?view=all"), apiFetch("/tasks?view=completed")]);
      setTasks(all || []); setDone(completed || []);
    } catch { setError(true); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <LoadingState label="Preparing your day…" />;
  if (error) return (
    <div className="my-4 flex flex-col items-center justify-center rounded-2xl border border-destructive/30 bg-destructive/5 p-12 text-center" data-testid="today-error">
      <h3 className="font-heading text-xl font-semibold text-foreground">Something went wrong</h3>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">We couldn't load your tasks just now.</p>
      <button onClick={load} className="mt-3 rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">Retry</button>
    </div>
  );

  const d = today();
  const needsAttention = tasks.filter((t) => t.status !== "done" && ((t.due_date && t.due_date < d) || t.priority === "urgent"));
  const naIds = new Set(needsAttention.map((t) => t.id));
  const dueToday = tasks.filter((t) => t.due_date === d && t.task_type !== "reminder" && !naIds.has(t.id) && t.status !== "done");
  const reminders = tasks.filter((t) => t.task_type === "reminder" && t.status !== "done" && !naIds.has(t.id));
  const waiting = tasks.filter((t) => t.waiting && t.status !== "done" && !naIds.has(t.id));
  const recent = done.filter((t) => (t.completed_at || "").slice(0, 10) === d).slice(0, 6);

  const total = needsAttention.length + dueToday.length + reminders.length + waiting.length + recent.length;
  if (total === 0) {
    return (
      <div className="my-4 flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-muted/10 p-12 text-center" data-testid="today-empty">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border/40 bg-secondary/60 text-primary shadow-soft"><Sparkles className="h-6 w-6" /></div>
        <h3 className="mt-4 font-heading text-2xl font-semibold text-foreground">A clear day</h3>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">Nothing needs your attention right now. Capture a task or take a mindful pause.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-up" data-testid="today-view">
      <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-r from-secondary/50 via-card to-accent-subtle/30 p-6 shadow-soft">
        <h2 className="font-heading text-2xl font-semibold text-foreground sm:text-3xl">{greeting()}</h2>
        <p className="mt-1 max-w-xl text-sm text-muted-foreground">Here's what needs you today. Everything else stays quietly out of the way.</p>
      </div>
      <Section title="Needs attention" icon={AlertCircle} tone="text-destructive" items={needsAttention} {...{ catName, onOpen, onComplete }} />
      <Section title="Due today" icon={CalendarClock} tone="text-primary" items={dueToday} {...{ catName, onOpen, onComplete }} />
      <Section title="Reminders" icon={Bell} tone="text-accent" items={reminders} {...{ catName, onOpen, onComplete }} />
      <Section title="Waiting / blocked" icon={Clock} tone="text-warning" items={waiting} {...{ catName, onOpen, onComplete }} />
      <Section title="Recently completed" icon={CheckCircle2} tone="text-success" items={recent} {...{ catName, onOpen, onComplete }} />
    </div>
  );
}

function Section({ title, icon: Icon, tone, items, catName, onOpen, onComplete }) {
  if (!items.length) return null;
  return (
    <section>
      <div className="mb-2 flex items-center gap-2 px-1">
        <Icon className={`h-4 w-4 ${tone}`} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
        <span className="text-[11px] text-muted-foreground/70">{items.length}</span>
      </div>
      <div className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/70 bg-card shadow-soft">
        {items.map((t) => <TaskRow key={t.id} t={t} catName={catName} onOpen={onOpen} onComplete={onComplete} />)}
      </div>
    </section>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}
